/**
 * Shared ELK layout engine for knowledge graph/map visualizations.
 * Replaces dagre with elkjs for proper layered layout with edge routing.
 */

import ELK from 'elkjs/lib/elk.bundled.js'

const elk = new ELK()

// Default ELK layout options for layered algorithm
const BASE_ELK_OPTIONS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.spacing.nodeNode': '50',
  'elk.layered.spacing.nodeNodeBetweenLayers': '80',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
  'elk.edgeRouting': 'ORTHOGONAL',
  'elk.spacing.edgeNode': '30',
  'elk.spacing.edgeEdge': '15',
  'elk.layered.spacing.edgeNodeBetweenLayers': '30',
  'elk.layered.spacing.edgeEdgeBetweenLayers': '15'
}

const GROUP_PADDING = '[top=40,left=20,bottom=20,right=20]'

/**
 * Build an ELK layout from nodes and edges.
 *
 * @param {Array} nodes - Array of node objects. Each must have a unique `id`,
 *   plus `domain`, `test_type`, `difficulty`, `skill` fields.
 * @param {Array} edges - Array of { id, source, target, type? } objects.
 *   `type` can be 'prerequisite' or 'related'.
 * @param {Object} options
 * @param {'domain'|'difficulty'|'none'} options.groupBy - How to group nodes
 * @param {'DOWN'|'RIGHT'} options.direction - Layout direction
 * @param {number} options.nodeWidth - Width of each node (px)
 * @param {number} options.nodeHeight - Default height of each node (px)
 * @param {boolean} options.compoundSkillGroups - If true, cluster E/M/H within skill groups (student view)
 * @param {Function} options.getNodeHeight - Optional fn(node) → height for dynamic node heights
 * @returns {Promise<{ positionedNodes: Array, routedEdges: Array, groupNodes: Array }>}
 */
export async function buildELKLayout(nodes, edges, options = {}) {
  const {
    groupBy = 'domain',
    direction = 'DOWN',
    nodeWidth = 220,
    nodeHeight = 100,
    compoundSkillGroups = false,
    getNodeHeight
  } = options

  if (!nodes.length) {
    return { positionedNodes: [], routedEdges: [], groupNodes: [] }
  }

  // Only use prerequisite edges for layout
  const layoutEdges = edges.filter((e) => e.type !== 'related')

  // Build the ELK graph hierarchy
  const elkGraph = buildElkGraph(nodes, layoutEdges, {
    groupBy,
    direction,
    nodeWidth,
    nodeHeight,
    compoundSkillGroups,
    getNodeHeight
  })

  // Run ELK layout
  const layoutResult = await elk.layout(elkGraph)

  // Flatten results into React Flow format
  return flattenElkResult(layoutResult, nodes, edges, {
    nodeWidth,
    nodeHeight,
    groupBy,
    getNodeHeight
  })
}

/**
 * Build the ELK graph hierarchy with optional grouping.
 */
function buildElkGraph(nodes, edges, options) {
  const { groupBy, direction, nodeWidth, nodeHeight, compoundSkillGroups, getNodeHeight } = options

  const elkOptions = {
    ...BASE_ELK_OPTIONS,
    'elk.direction': direction
  }

  if (groupBy !== 'none') {
    elkOptions['elk.hierarchyHandling'] = 'INCLUDE_CHILDREN'
  }

  const graph = {
    id: 'root',
    layoutOptions: elkOptions,
    children: [],
    edges: []
  }

  if (groupBy === 'none') {
    // Flat layout — no groups
    graph.children = nodes.map((node) => ({
      id: node.id,
      width: nodeWidth,
      height: getNodeHeight ? getNodeHeight(node) : nodeHeight
    }))
  } else if (compoundSkillGroups && groupBy !== 'difficulty') {
    // Student view: group by domain, then cluster E/M/H within skill
    buildStudentHierarchy(graph, nodes, nodeWidth, nodeHeight, getNodeHeight)
  } else {
    // Admin view: group by domain or difficulty
    buildGroupedHierarchy(graph, nodes, groupBy, nodeWidth, nodeHeight, getNodeHeight)
  }

  // Add edges — use compound-aware source/target
  const nodeIdSet = new Set(nodes.map((n) => n.id))
  for (const edge of edges) {
    if (nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target)) {
      graph.edges.push({
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target]
      })
    }
  }

  return graph
}

/**
 * Student hierarchy: domain groups → skill clusters → E/M/H nodes
 */
function buildStudentHierarchy(graph, nodes, nodeWidth, nodeHeight, getNodeHeight) {
  const domainMap = new Map()
  for (const node of nodes) {
    const domainKey = `${node.test_type}::${node.domain}`
    if (!domainMap.has(domainKey)) {
      domainMap.set(domainKey, {
        test_type: node.test_type,
        domain: node.domain,
        nodes: []
      })
    }
    domainMap.get(domainKey).nodes.push(node)
  }

  // Sort: math first, then alpha
  const sortedDomains = [...domainMap.entries()].sort(([, a], [, b]) => {
    if (a.test_type !== b.test_type) return a.test_type === 'math' ? -1 : 1
    return a.domain.localeCompare(b.domain)
  })

  for (const [domainKey, group] of sortedDomains) {
    const domainGroup = {
      id: `group_${domainKey}`,
      layoutOptions: {
        'elk.padding': GROUP_PADDING,
        'elk.spacing.nodeNode': '40',
        'elk.layered.spacing.nodeNodeBetweenLayers': '60'
      },
      children: [],
      edges: []
    }

    // Cluster by skill within domain
    const skillMap = new Map()
    for (const node of group.nodes) {
      const skillKey = `${node.domain}:${node.skill}`
      if (!skillMap.has(skillKey)) skillMap.set(skillKey, [])
      skillMap.get(skillKey).push(node)
    }

    for (const [, skillNodes] of skillMap) {
      for (const node of skillNodes) {
        domainGroup.children.push({
          id: node.id,
          width: nodeWidth,
          height: getNodeHeight ? getNodeHeight(node) : nodeHeight
        })
      }
    }

    graph.children.push(domainGroup)
  }
}

/**
 * Admin hierarchy: group by domain or difficulty
 */
function buildGroupedHierarchy(graph, nodes, groupBy, nodeWidth, nodeHeight, getNodeHeight) {
  const groupMap = new Map()

  for (const node of nodes) {
    let groupKey
    if (groupBy === 'domain') {
      groupKey = `${node.test_type}::${node.domain}`
    } else if (groupBy === 'difficulty') {
      groupKey = node.difficulty
    } else {
      groupKey = 'all'
    }

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, [])
    }
    groupMap.get(groupKey).push(node)
  }

  // Sort groups
  const sortedKeys = [...groupMap.keys()].sort((a, b) => {
    if (groupBy === 'difficulty') {
      const order = { E: 0, M: 1, H: 2 }
      return (order[a] ?? 0) - (order[b] ?? 0)
    }
    return a.localeCompare(b)
  })

  for (const groupKey of sortedKeys) {
    const groupNodes = groupMap.get(groupKey)
    const groupElk = {
      id: `group_${groupKey}`,
      layoutOptions: {
        'elk.padding': GROUP_PADDING,
        'elk.spacing.nodeNode': '40',
        'elk.layered.spacing.nodeNodeBetweenLayers': '60'
      },
      children: groupNodes.map((node) => ({
        id: node.id,
        width: nodeWidth,
        height: getNodeHeight ? getNodeHeight(node) : nodeHeight
      })),
      edges: []
    }
    graph.children.push(groupElk)
  }
}

/**
 * Flatten ELK layout result into React Flow nodes, edges, and group nodes.
 */
function flattenElkResult(layoutResult, originalNodes, originalEdges, options) {
  const { nodeWidth, nodeHeight, groupBy, getNodeHeight } = options
  const nodeDataMap = new Map(originalNodes.map((n) => [n.id, n]))

  const positionedNodes = []
  const groupNodes = []

  // Process the layout result recursively
  function processChildren(elkNode, parentX = 0, parentY = 0) {
    if (!elkNode.children) return

    for (const child of elkNode.children) {
      const absX = parentX + (child.x || 0)
      const absY = parentY + (child.y || 0)

      if (child.id.startsWith('group_')) {
        // This is a group node
        const groupKey = child.id.replace('group_', '')
        const groupInfo = parseGroupKey(groupKey, groupBy, originalNodes)

        groupNodes.push({
          id: child.id,
          type: 'domainGroup',
          position: { x: absX, y: absY },
          data: {
            label: groupInfo.label,
            test_type: groupInfo.test_type,
            nodeCount: child.children?.length || 0,
            width: child.width,
            height: child.height
          },
          style: {
            width: child.width,
            height: child.height,
            zIndex: -1
          },
          selectable: false,
          draggable: false
        })

        // Process children within this group
        processChildren(child, absX, absY)
      } else {
        // This is a regular node
        const nodeData = nodeDataMap.get(child.id)
        if (nodeData) {
          const h = getNodeHeight ? getNodeHeight(nodeData) : nodeHeight
          positionedNodes.push({
            id: child.id,
            type: nodeData._nodeType || 'knowledgeNode',
            position: { x: absX, y: absY },
            data: { ...nodeData, height: h },
            parentNode: undefined, // We use absolute positioning
            extent: undefined
          })
        }
      }
    }
  }

  processChildren(layoutResult)

  // Process edges — extract ELK routing bend points
  const routedEdges = buildRoutedEdges(layoutResult, originalEdges, positionedNodes, {
    nodeWidth,
    nodeHeight,
    getNodeHeight,
    nodeDataMap
  })

  return { positionedNodes, routedEdges, groupNodes }
}

/**
 * Parse a group key back into display info.
 */
function parseGroupKey(groupKey, groupBy, nodes) {
  if (groupBy === 'difficulty') {
    const labels = { E: 'Easy', M: 'Medium', H: 'Hard' }
    return { label: labels[groupKey] || groupKey, test_type: null }
  }

  // Domain grouping: key is "test_type::domain"
  const sepIdx = groupKey.indexOf('::')
  const testType = sepIdx >= 0 ? groupKey.slice(0, sepIdx) : groupKey
  const domain = sepIdx >= 0 ? groupKey.slice(sepIdx + 2) : ''

  // Try to get test_type from the nodes in this group
  const node = nodes.find((n) => `${n.test_type}::${n.domain}` === groupKey)

  return {
    label: domain || groupKey,
    test_type: node?.test_type || testType
  }
}

/**
 * Build routed edges from ELK's layout result.
 * ELK provides edge sections with start/end/bend points.
 */
function buildRoutedEdges(layoutResult, originalEdges, positionedNodes, options) {
  const { nodeWidth, nodeHeight, getNodeHeight, nodeDataMap } = options

  // Collect ELK edge routing info
  const elkEdgeMap = new Map()
  collectElkEdges(layoutResult, elkEdgeMap, 0, 0)

  const nodePositionMap = new Map(positionedNodes.map((n) => [n.id, n.position]))

  return originalEdges.map((edge) => {
    const elkEdge = elkEdgeMap.get(edge.id)
    const isPrerequisite = edge.type !== 'related'

    const baseStyle = isPrerequisite
      ? { stroke: '#6366f1', strokeWidth: 2 }
      : { stroke: '#9ca3af', strokeWidth: 1, strokeDasharray: '5,5' }

    const result = {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'routedEdge',
      data: {
        bendPoints: [],
        isPrerequisite,
        edgeType: edge.type || 'prerequisite'
      },
      style: baseStyle,
      markerEnd: isPrerequisite ? { type: 'arrowclosed', color: '#6366f1' } : undefined
    }

    if (elkEdge?.sections?.length) {
      // Use ELK's computed routing
      const section = elkEdge.sections[0]
      const points = []

      if (section.startPoint) {
        points.push(section.startPoint)
      }
      if (section.bendPoints) {
        points.push(...section.bendPoints)
      }
      if (section.endPoint) {
        points.push(section.endPoint)
      }

      result.data.bendPoints = points
    } else {
      // Fallback: compute simple path from node positions
      const sourcePos = nodePositionMap.get(edge.source)
      const targetPos = nodePositionMap.get(edge.target)
      if (sourcePos && targetPos) {
        const sourceData = nodeDataMap.get(edge.source)
        const sourceH = getNodeHeight && sourceData ? getNodeHeight(sourceData) : nodeHeight
        result.data.bendPoints = [
          { x: sourcePos.x + nodeWidth / 2, y: sourcePos.y + sourceH },
          { x: targetPos.x + nodeWidth / 2, y: targetPos.y }
        ]
      }
    }

    return result
  })
}

/**
 * Recursively collect edge sections from ELK result.
 */
function collectElkEdges(elkNode, edgeMap, parentX, parentY) {
  const offsetX = parentX + (elkNode.x || 0)
  const offsetY = parentY + (elkNode.y || 0)

  // Only apply offset for non-root nodes
  const applyX = elkNode.id === 'root' ? 0 : offsetX
  const applyY = elkNode.id === 'root' ? 0 : offsetY

  if (elkNode.edges) {
    for (const edge of elkNode.edges) {
      if (edge.sections) {
        // Offset the edge points by the parent's absolute position
        const offsetSections = edge.sections.map((section) => ({
          startPoint: section.startPoint
            ? { x: section.startPoint.x + applyX, y: section.startPoint.y + applyY }
            : null,
          endPoint: section.endPoint
            ? { x: section.endPoint.x + applyX, y: section.endPoint.y + applyY }
            : null,
          bendPoints: (section.bendPoints || []).map((bp) => ({
            x: bp.x + applyX,
            y: bp.y + applyY
          }))
        }))
        edgeMap.set(edge.id, { ...edge, sections: offsetSections })
      }
    }
  }

  if (elkNode.children) {
    for (const child of elkNode.children) {
      collectElkEdges(child, edgeMap, applyX, applyY)
    }
  }
}
